import React, { ChangeEvent, useMemo, useState } from 'react'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'

import { getImageDimensions } from './getImageDimensions'

export type UploadImageRequestConfig = {
    id: string
    file: File
    imageUrl: string
    type: 'spaceIcon' | 'avatar'
    setProgress: (progress: number) => void
}

type FetchImageResourceType = 'spaceIcon' | 'avatar'

export type UseOnImageChangeEventProps<T extends FieldValues> = {
    formFieldName: Path<T>
    resourceId: string
    type: FetchImageResourceType
    imageRestrictions?: {
        maxSize?: {
            message: string
            max: number
        }
        minDimension?: {
            message: string
            min: number
        }
    }
    onUploadImage?: (args: UploadImageRequestConfig) => void
    onFileAdded?: () => void
} & Pick<UseFormReturn<T>, 'setError' | 'clearErrors' | 'formState'>

export function useOnImageChangeEvent<T extends FieldValues>({
    resourceId,
    imageRestrictions,
    formFieldName,
    setError,
    clearErrors,
    type,
    onUploadImage,
    onFileAdded,
}: UseOnImageChangeEventProps<T>) {
    const _resourceId = useMemo(() => resourceId, [resourceId])
    const {
        maxSize = {
            message: 'Upload an image less than 25MB',
            max: 25_000_000,
        },
        minDimension = {
            message: `Image is too small. Please upload an image with a minimum height & width of 400px.`,
            min: 400,
        },
    } = imageRestrictions ?? {}

    const [uploadProgress, setUploadProgress] = useState<number>(0)

    const isUploading = useMemo(() => uploadProgress > 0 && uploadProgress < 100, [uploadProgress])

    async function onChange(e: ChangeEvent<HTMLInputElement>) {
        const files = e.target.files
        if (files && files.length > 0) {
            const url = URL.createObjectURL(files[0])
            const { width, height } = await getImageDimensions(url)
            clearErrors?.([formFieldName])
            onFileAdded?.()

            if (files[0].size > maxSize.max) {
                setError?.(formFieldName, {
                    type: 'required',
                    message: maxSize.message,
                })
                return
            }

            if (width < minDimension.min || height < minDimension.min) {
                popupToast(
                    ({ toast }) => (
                        <StandardToast.Error message={minDimension.message} toast={toast} />
                    ),
                    { id: 'image-dimension-error' },
                )
                return
            }

            onUploadImage?.({
                id: _resourceId,
                file: files[0],
                imageUrl: url,
                type,
                setProgress: setUploadProgress,
            })
        }
    }

    return {
        onChange,
        isUploading,
    }
}
